import React from "react";
import { useDispatch } from "react-redux";
import { KTCard } from "../../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../../_metronic/helpers/components/KTCardBody";
import CustomNumberInput from "../../../../components/CustomNumberInput";
import CustomSwitch from "../../../../components/CustomSwitch";
import { storeEbbeModalCallback } from "../../../../redux/reducers/authSlice";
import { GetAmbarByIdResponseOvertimePersonnel, GetAmbarByIdResponsePorteragePersonnel } from "../../../../models/response/ambar/GetAmbarByIdResponse";
import PersonelSatirTablosu from "./PersonelSatirTablosu";

interface Props {
    operationalInformationRef: any
    waitTime: string | undefined
    setWaitTime: React.Dispatch<React.SetStateAction<string | undefined>>
    isBridge: boolean
    setIsBridge: React.Dispatch<React.SetStateAction<boolean>>
    isAdditionalLocation: boolean
    setIsAdditionalLocation: React.Dispatch<React.SetStateAction<boolean>>
    isOvertimeTravelMeal: boolean
    setIsOvertimeTravelMeal: React.Dispatch<React.SetStateAction<boolean>>
    overtimePersonnels: GetAmbarByIdResponseOvertimePersonnel[]
    setOvertimePersonnels: React.Dispatch<React.SetStateAction<GetAmbarByIdResponseOvertimePersonnel[]>>
    isPorterage: boolean
    setIsPorterage: React.Dispatch<React.SetStateAction<boolean>>
    porteragePersonnels: GetAmbarByIdResponsePorteragePersonnel[]
    setPorteragePersonnels: React.Dispatch<React.SetStateAction<GetAmbarByIdResponsePorteragePersonnel[]>>
}

/**
 * Siparişin operasyonel alanları: Bekleme Süresi, Hammaliye ve üç anahtar.
 * "Mesai - Yol - Yemek" açıkken mesai yapan personel, "Hammaliye" açıkken hammaliye personeli tablosu görünür.
 */
const OperationalInformation = ({
    operationalInformationRef,
    waitTime,
    setWaitTime,
    isBridge,
    setIsBridge,
    isAdditionalLocation,
    setIsAdditionalLocation,
    isOvertimeTravelMeal,
    setIsOvertimeTravelMeal,
    overtimePersonnels,
    setOvertimePersonnels,
    isPorterage,
    setIsPorterage,
    porteragePersonnels,
    setPorteragePersonnels,
}: Props) => {
    const dispatch = useDispatch();

    /**
     * Anahtar kapatılırken girilmiş satır varsa onay alınır. Vazgeçilirse anahtar
     * açık kalır; onaylanırsa satırlar silinir (karar: 02.10.2026).
     */
    const anahtarDegis = (
        _acikMi: boolean,
        _setAcik: (_deger: boolean) => void,
        _satirSayisi: number,
        _satirlariSil: () => void
    ): React.Dispatch<React.SetStateAction<boolean>> => (_deger) => {
        const _acik = typeof _deger == "function" ? _deger(_acikMi) : _deger;

        if (_acik) {
            _setAcik(true);
            return;
        }

        if (_satirSayisi == 0) {
            _setAcik(false);
            return;
        }

        dispatch(storeEbbeModalCallback({
            title: 'Bilgilendirme',
            body: `Girilmiş ${_satirSayisi} personel satırı silinecek. Emin misiniz?`,
            buttons: [
                { text: 'Vazgeç', btnClass: 'btn-warning' },
                {
                    text: 'Evet, sil',
                    btnClass: 'btn-danger',
                    onClick: () => {
                        _satirlariSil();
                        _setAcik(false);
                    }
                }
            ]
        }))
    }

    const mesaiDegis = anahtarDegis(isOvertimeTravelMeal, setIsOvertimeTravelMeal, overtimePersonnels.length, () => setOvertimePersonnels([]));
    const hammaliyeDegis = anahtarDegis(isPorterage, setIsPorterage, porteragePersonnels.length, () => setPorteragePersonnels([]));

    return (
        <div className='containerPart' ref={operationalInformationRef}>
            <KTCard className="ebbe-card mt-10">
                <KTCardBody>
                    <h4 className="mb-10 text-black">Operasyonel Bilgiler</h4>

                    <div className="row">
                        <div className="col-md-4">
                            <CustomNumberInput labelText="Bekleme Süresi" value={waitTime} setValue={setWaitTime} />
                        </div>

                        <div className="col-md-4">
                            <CustomSwitch labelText="Hammaliye" isActive={isPorterage} setIsActive={hammaliyeDegis} />
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-4">
                            <CustomSwitch labelText="Mesai - Yol - Yemek" isActive={isOvertimeTravelMeal} setIsActive={mesaiDegis} />
                        </div>

                        <div className="col-md-4">
                            <CustomSwitch labelText="Köprü Geçiş" isActive={isBridge} setIsActive={setIsBridge} />
                        </div>

                        <div className="col-md-4">
                            <CustomSwitch labelText="Ek Lokasyon" isActive={isAdditionalLocation} setIsActive={setIsAdditionalLocation} />
                        </div>
                    </div>

                    {
                        isOvertimeTravelMeal && (
                            <PersonelSatirTablosu
                                baslik="Mesai Yapan Personel"
                                miktarBasligi="Mesai Süresi (Saat)"
                                birim="saat"
                                satirlar={overtimePersonnels}
                                setSatirlar={setOvertimePersonnels}
                                miktarAlani="hours"
                                yeniSatir={() => ({
                                    ambarOvertimePersonnelId: undefined,
                                    personnelId: undefined,
                                    personnelName: undefined,
                                    supplierCompanyName: undefined,
                                    hours: '',
                                    description: '',
                                })}
                            />
                        )
                    }

                    {
                        isPorterage && (
                            <PersonelSatirTablosu
                                baslik="Hammaliye Personeli"
                                miktarBasligi="Hammaliye (Gün)"
                                birim="gün"
                                satirlar={porteragePersonnels}
                                setSatirlar={setPorteragePersonnels}
                                miktarAlani="days"
                                yeniSatir={() => ({
                                    ambarPorteragePersonnelId: undefined,
                                    personnelId: undefined,
                                    personnelName: undefined,
                                    supplierCompanyName: undefined,
                                    days: '',
                                    description: '',
                                })}
                            />
                        )
                    }
                </KTCardBody>
            </KTCard>
        </div>
    )
}

export default OperationalInformation;
